import React, { useEffect, createElement, Component } from 'react';
import { Star, Clock, User, ArrowRight, CheckCircle2, Menu } from 'lucide-react';
// --- Components ---
const CTAButton = ({
  children,
  className = '',
  fullWidth = false
}: {
  children: React.ReactNode;
  className?: string;
  fullWidth?: boolean;
}) => <a href="https://angelitasgummy.com/products/angelitas" target="_blank" rel="noopener noreferrer" className={`
      ${fullWidth ? 'w-full' : 'w-auto'}
      bg-[#be6e8a] hover:bg-[#a85a75] text-white 
      font-bold py-4 px-6 rounded-sm shadow-sm 
      transition-all duration-200 ease-in-out 
      transform hover:-translate-y-0.5
      uppercase tracking-wide text-sm md:text-base
      flex items-center justify-center gap-2
      ${className}
    `} data-id="element-2">
    {children}
    <ArrowRight className="w-5 h-5" data-id="element-3" />
  </a>;
const TestimonialCard = ({
  name,
  location,
  quote,
  rating = 5,
  avatarUrl
}: {
  name: string;
  location: string;
  quote: string;
  rating?: number;
  avatarUrl?: string;
}) => <div className="bg-stone-50 p-6 border-l-4 border-[#be6e8a] my-6" data-id="element-4">
    <div className="flex gap-1 mb-3 text-[#be6e8a]" data-id="element-5">
      {[...Array(rating)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" data-id="element-6" />)}
    </div>
    <p className="text-slate-700 italic mb-4 text-lg" data-id="element-7">"{quote}"</p>
    <div className="flex items-center gap-3" data-id="element-8">
      <div className="w-10 h-10 bg-slate-200 rounded-full overflow-hidden flex items-center justify-center text-slate-500" data-id="element-9">
        {avatarUrl ? <img src={avatarUrl} alt={name} className="w-full h-full object-cover" data-id="element-10" /> : <User className="w-6 h-6" data-id="element-11" />}
      </div>
      <div data-id="element-12">
        <p className="font-bold text-slate-900 text-sm" data-id="element-13">{name}</p>
        <p className="text-slate-500 text-xs uppercase tracking-wider" data-id="element-14">
          {location}
        </p>
      </div>
    </div>
  </div>;
const PullQuote = ({
  children
}: {
  children: React.ReactNode;
}) => <blockquote className="my-10 border-t-2 border-b-2 border-stone-200 py-8 text-center" data-id="element-15">
    <p className="font-serif text-2xl md:text-3xl text-slate-800 italic leading-relaxed" data-id="element-16">
      {children}
    </p>
  </blockquote>;
const SectionHeading = ({
  children
}: {
  children: React.ReactNode;
}) => <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-12 mb-6 leading-tight" data-id="element-17">
    {children}
  </h2>;
// --- Main Page ---
export function AdvertorialPage() {
  useEffect(() => {
    // Facebook Pixel Code - Robust implementation with error handling
    if (typeof window === 'undefined') return;
    try {
      // Initialize fbq function queue
      if (!(window as any).fbq) {
        const fbq: any = function () {
          fbq.callMethod ? fbq.callMethod.apply(fbq, arguments) : fbq.queue.push(arguments);
        };
        fbq.push = fbq;
        fbq.loaded = true;
        fbq.version = '2.0';
        fbq.queue = [];
        (window as any).fbq = fbq;
      }
      // Load the Facebook Pixel script
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://connect.facebook.net/en_US/fbevents.js';
      script.onload = () => {
        try {
          if ((window as any).fbq) {
            ;
            (window as any).fbq('init', '1604022217679763');
            (window as any).fbq('track', 'PageView');
          }
        } catch (error) {
          console.warn('Facebook Pixel initialization failed:', error);
        }
      };
      script.onerror = () => {
        console.warn('Facebook Pixel script failed to load (may be blocked by ad blocker)');
      };
      document.head.appendChild(script);
    } catch (error) {
      console.warn('Facebook Pixel setup failed:', error);
    }
  }, []);
  return <div className="min-h-screen bg-white" data-id="element-18">
      {/* Top Navigation */}
      <nav className="sticky top-0 z-50 bg-white border-b border-stone-200 shadow-sm" data-id="element-19">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between" data-id="element-20">
          <div className="font-serif text-xl md:text-2xl font-bold text-slate-900 tracking-tight" data-id="element-21">
            Salud y Bienestar
          </div>
          <a href="https://angelitasgummy.com/products/angelitas" target="_blank" rel="noopener noreferrer" className="bg-[#be6e8a] text-white px-4 py-2 text-sm font-semibold uppercase tracking-wider hover:bg-[#a85a75] transition-colors" data-id="element-22">
            Ver Angelitas
          </a>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-4 py-8 md:py-12" data-id="element-23">
        {/* Article Header */}
        <header className="mb-10" data-id="element-24">
          <div className="flex items-center gap-2 text-xs md:text-sm text-[#be6e8a] font-bold uppercase tracking-widest mb-6" data-id="element-25">
            <span data-id="element-26">Salud</span>
            <span className="text-stone-300" data-id="element-27">•</span>
            <span data-id="element-28">Testimonios</span>
          </div>

          <h1 className="text-3xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight" data-id="element-29">
            Mujer De 48 Años Revela: "Creí Que Mi Cansancio Era Normal — Mi
            Doctora Me Dijo Que Estuve A Punto De Un Derrame"
          </h1>

          <p className="text-lg md:text-xl text-slate-600 leading-relaxed mb-8 font-serif italic" data-id="element-30">
            Gloria Sánchez tiene 48 años y vive en Guadalajara. Hasta hace 8
            meses, estaba convencida de que iba a morirse cansada.
          </p>

          <div className="flex items-center justify-between border-t border-b border-stone-100 py-4" data-id="element-31">
            <div className="flex items-center gap-3" data-id="element-32">
              <div className="w-12 h-12 bg-stone-200 rounded-full overflow-hidden" data-id="element-33">
                <img src="https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=100&h=100&q=80" alt="Author" className="w-full h-full object-cover opacity-80 grayscale" data-id="element-34" />
              </div>
              <div data-id="element-35">
                <p className="text-xs font-bold text-slate-900 uppercase tracking-wider" data-id="element-36">
                  Por María Fernanda
                </p>
                <p className="text-xs text-slate-500" data-id="element-37">Publicado hace 2 días</p>
              </div>
            </div>
            <div className="flex items-center gap-1 text-slate-400 text-xs" data-id="element-38">
              <Clock className="w-4 h-4" data-id="element-39" />
              <span data-id="element-40">5 min de lectura</span>
            </div>
          </div>
        </header>

        {/* Hero Image */}
        <figure className="mb-12" data-id="element-41">
          <div className="w-full aspect-[16/9] bg-stone-200 relative overflow-hidden rounded-sm" data-id="element-42">
            <img src="https://cdn.magicpatterns.com/uploads/3f3FcjnHn4nQaKgrBSaMEM/TESTS_-_Arcads_image_asset_(18).png" alt="Gloria Sánchez monitoreando su presión arterial" className="w-full h-full object-cover" data-id="element-43" />
          </div>
          <figcaption className="mt-3 text-xs text-slate-500 text-center uppercase tracking-wider" data-id="element-44">
            Gloria Sánchez recuperó su energía y salud cardiovascular de forma
            natural.
          </figcaption>
        </figure>

        {/* Article Body */}
        <article className="prose prose-lg prose-slate max-w-none text-slate-700" data-id="element-45">
          <p className="lead first-letter:text-5xl first-letter:font-serif first-letter:font-bold first-letter:mr-2 first-letter:float-left first-letter:leading-none text-slate-900" data-id="element-46">
            "No es exageración", me dice. "Necesitaba 4 cafés para funcionar y
            ni así. Me levantaba agotada, como si no hubiera dormido. Las
            piernas me pesaban como plomo desde las 4 de la tarde. Se me
            olvidaban las palabras a media plática."
          </p>

          <p data-id="element-47">
            Gloria es madre de tres. Trabaja medio tiempo en una papelería.
            Cuida a su mamá de 74 años. Es el eje de su hogar — la que mantiene
            todo funcionando aunque por dentro se esté cayendo a pedazos.
          </p>

          <p data-id="element-48">
            "Mis hijas me decían: 'Mamá, ya no sales ni al mandado'. Y tenían
            razón. Pero yo pensaba: es normal, ya tengo casi 50, así se siente."
          </p>

          <p className="font-bold text-slate-900" data-id="element-49">
            Lo que Gloria no sabía es que su cuerpo le estaba mandando señales
            de emergencia.
          </p>

          <p data-id="element-50">Señales que casi le cuestan la vida.</p>

          <SectionHeading data-id="element-51">El Día Que Casi No Cuenta</SectionHeading>

          <p data-id="element-52">
            Un martes de marzo, Gloria se levantó mareada. Sintió presión en el
            pecho. Dejó a su hija en la escuela y en el camino de regreso casi
            se desmaya manejando.
          </p>

          <p data-id="element-53">
            "Alcancé a orillarme. Me quedé agarrada del volante, temblando."
          </p>

          <p data-id="element-54">
            Su esposo la llevó de emergencia con la doctora. La Dra. Ramírez le
            tomó la presión y se quedó callada.
          </p>

          <div className="bg-red-50 p-6 rounded-sm border border-red-100 my-8 text-center" data-id="element-55">
            <span className="block text-5xl font-bold text-red-800 mb-2 font-serif" data-id="element-56">
              165/105
            </span>
            <span className="text-red-600 text-sm uppercase tracking-wider font-bold" data-id="element-57">
              Presión Arterial Crítica
            </span>
          </div>

          <p data-id="element-58">
            Para contexto: lo normal es 120/80. Arriba de 140/90 es
            hipertensión. Lo de Gloria era una crisis.
          </p>

          <p data-id="element-59">La doctora la miró seria:</p>

          <p className="italic text-slate-800 border-l-4 border-slate-300 pl-4 py-1" data-id="element-60">
            "Gloria, estuviste a un paso de un derrame cerebral. Si esto hubiera
            subido un poco más mientras manejabas... no estarías aquí."
          </p>

          <p data-id="element-61">Gloria se quedó helada. "¿Cómo? Si yo solo estaba cansada..."</p>

          <SectionHeading data-id="element-62">
            El "Asesino Silencioso" Que Nadie Le Explicó
          </SectionHeading>

          <p data-id="element-63">
            La doctora le mostró las estadísticas: 42% de las mujeres mexicanas
            tienen presión alta. Pero el 65% no lo saben. Le dicen "el asesino
            silencioso" porque no avisa.
          </p>

          <p data-id="element-64">
            "Yo era parte de ese 65%", dice Gloria. "Llevaba AÑOS sintiéndome
            mal y pensando que era normal."
          </p>

          <p data-id="element-65">Pero había algo que Gloria no entendía:</p>

          <p data-id="element-66">
            "Doctora, ¿qué tiene que ver mi presión con el cansancio? Yo vine
            porque no tengo energía, no porque me duela nada."
          </p>

          <p data-id="element-67">La respuesta la dejó sin palabras.</p>

          <SectionHeading data-id="element-68">La Verdad Sobre Su Agotamiento</SectionHeading>

          <p data-id="element-69">La Dra. Ramírez le explicó algo que cambió todo:</p>

          <p data-id="element-70">
            "Después de los 40 — y especialmente con la menopausia — tu cuerpo
            deja de producir una molécula llamada <strong data-id="element-71">óxido nítrico</strong>
            . Puede caer hasta un 50%. Tres científicos ganaron el Premio Nobel
            en 1998 por descubrir lo importante que es."
          </p>

          <PullQuote data-id="element-72">
            "El óxido nítrico le dice a tus arterias que se relajen y dejen
            pasar la sangre. Sin él, las arterias se ponen rígidas."
          </PullQuote>

          <p data-id="element-73">
            "Si la sangre no llega bien a tus piernas, se sienten pesadas y se
            hinchan. Si no llega bien a tu cerebro, te sientes cansada y con la
            mente nublada. Si el corazón tiene que trabajar de más, la presión
            sube."
          </p>

          <p data-id="element-74">Gloria conectó los puntos.</p>

          <ul className="list-none pl-0 space-y-3 my-8 font-medium text-slate-800" data-id="element-75">
            <li className="flex items-center gap-3" data-id="element-76">
              <CheckCircle2 className="w-5 h-5 text-red-500" data-id="element-77" /> El cansancio.
            </li>
            <li className="flex items-center gap-3" data-id="element-78">
              <CheckCircle2 className="w-5 h-5 text-red-500" data-id="element-79" /> Las piernas de
              plomo.
            </li>
            <li className="flex items-center gap-3" data-id="element-80">
              <CheckCircle2 className="w-5 h-5 text-red-500" data-id="element-81" /> La niebla
              mental.
            </li>
            <li className="flex items-center gap-3" data-id="element-82">
              <CheckCircle2 className="w-5 h-5 text-red-500" data-id="element-83" /> La hinchazón.
            </li>
          </ul>

          <p data-id="element-84">
            No era la edad. Era su sistema circulatorio apagándose en silencio.
          </p>

          <SectionHeading data-id="element-85">Por Qué Nada Le Había Funcionado</SectionHeading>

          <p data-id="element-86">Gloria había probado de todo:</p>

          <div className="grid gap-4 my-6" data-id="element-87">
            <div className="bg-stone-50 p-4 rounded-sm" data-id="element-88">
              <span className="font-bold text-slate-900" data-id="element-89">
                Geles de castaño de indias:
              </span>{' '}
              "Alivio por 20 minutos y ya. El problema estaba adentro."
            </div>
            <div className="bg-stone-50 p-4 rounded-sm" data-id="element-90">
              <span className="font-bold text-slate-900" data-id="element-91">
                Medias de compresión:
              </span>{' '}
              "Calurosas, incómodas, me hacían sentir vieja."
            </div>
            <div className="bg-stone-50 p-4 rounded-sm" data-id="element-92">
              <span className="font-bold text-slate-900" data-id="element-93">
                Jugos de betabel:
              </span>{' '}
              "Sabían a tierra, manchaban todo, y no vi cambios."
            </div>
            <div className="bg-stone-50 p-4 rounded-sm" data-id="element-94">
              <span className="font-bold text-slate-900" data-id="element-95">Vueltas al IMSS:</span>{' '}
              "Horas de espera para que me dijeran 'es estrés, señora'."
            </div>
          </div>

          <p data-id="element-96">
            Nada funcionaba porque nada atacaba la raíz: la falta de óxido
            nítrico. Era como ponerle curitas a una tubería rota.
          </p>

          <SectionHeading data-id="element-97">La Pista Que Lo Cambió Todo</SectionHeading>

          <p data-id="element-98">
            La Dra. Ramírez le explicó que hay formas naturales de restaurar el
            óxido nítrico.
          </p>

          <p data-id="element-99">
            "El betabel tiene compuestos que tu cuerpo convierte directamente en
            óxido nítrico — sin depender de las arterias dañadas. Pero el jugo
            casero no sirve porque no controlas la dosis."
          </p>

          <p data-id="element-100">
            "Lo que necesitas es un extracto concentrado. Y los mejores
            resultados combinan betabel con semilla de uva — uno produce óxido
            nítrico rápido, el otro repara las arterias para que vuelvan a
            producirlo solas."
          </p>

          <p data-id="element-101">
            Esa noche, Gloria buscó en internet y encontró testimonios de
            mujeres mexicanas hablando de unas gomitas con exactamente esa
            combinación:
          </p>

          <div className="bg-[#be6e8a]/10 p-6 rounded-sm border border-[#be6e8a]/20 my-8 text-center" data-id="element-102">
            <p className="font-bold text-[#8a4b61] text-lg" data-id="element-103">
              500mg de betabel + 150mg de semilla de uva (Enovita®)
            </p>
            <p className="text-2xl font-serif font-bold text-[#be6e8a] mt-2" data-id="element-104">
              Se llamaban Angelitas.
            </p>
          </div>

          <figure className="my-8" data-id="element-105">
            <img src="https://cdn.magicpatterns.com/uploads/o6wGTnfootnWqBB6GgB6s7/Captura_de_pantalla_2026-01-23_a_las_23.07.10.png" alt="Angelitas Gummy - Gomitas de betabel y semilla de uva" className="w-full max-w-2xl mx-auto rounded-sm shadow-lg" data-id="element-106" />
          </figure>

          <p data-id="element-107">
            "Tenían garantía de devolución. Pensé: si no funciona, pido mi
            dinero. ¿Qué puedo perder?"
          </p>

          <p data-id="element-108">Hizo el pedido.</p>

          <div className="my-12" data-id="element-109">
            <CTAButton fullWidth data-id="element-110">
              HAZ CLIC AQUÍ PARA CONOCER ANGELITAS
            </CTAButton>
          </div>

          <SectionHeading data-id="element-111">Lo Que Pasó Después</SectionHeading>

          <div className="space-y-8" data-id="element-112">
            <div data-id="element-113">
              <h3 className="font-bold text-lg text-[#be6e8a] uppercase tracking-wider mb-2" data-id="element-114">
                Semana 1
              </h3>
              <p data-id="element-115">
                Los primeros 3 días, nada. Gloria pensó: "Ya sabía, otra cosa
                que no sirve."
              </p>
              <p data-id="element-116">
                Pero el día 5, despertó diferente. "No increíble, pero tampoco
                destruida. Como si hubiera dormido de verdad."
              </p>
              <p data-id="element-117">
                Para el día 7, caminó de la papelería a su casa y se dio cuenta
                de algo: no había pensado en sus piernas. Normalmente era lo
                único que sentía.
              </p>
            </div>

            <div data-id="element-118">
              <h3 className="font-bold text-lg text-[#be6e8a] uppercase tracking-wider mb-2" data-id="element-119">
                Semana 2
              </h3>
              <p data-id="element-120">
                Su esposo le preguntó si había cambiado de café. "Estás como más
                activa."
              </p>
              <p data-id="element-121">
                Gloria no había cambiado de café. Lo que cambió es que ya no
                necesitaba el tercero.
              </p>
              <p data-id="element-122">
                Esa semana, se vio los pies antes de dormir. No estaban
                hinchados. Los dedos se veían normales — sin la forma de
                "salchicha" de siempre.
              </p>
            </div>

            <div data-id="element-123">
              <h3 className="font-bold text-lg text-[#be6e8a] uppercase tracking-wider mb-2" data-id="element-124">
                Semana 3
              </h3>
              <p data-id="element-125">
                Subió las 14 escaleras de su casa sin jadear. Recordó un número
                de teléfono sin anotarlo. Su anillo de bodas le quedó bien todo
                el día.
              </p>
            </div>

            <div data-id="element-126">
              <h3 className="font-bold text-lg text-[#be6e8a] uppercase tracking-wider mb-2" data-id="element-127">
                Semana 4
              </h3>
              <p data-id="element-128">
                Fue al chequeo médico. La enfermera le tomó la presión. Gloria
                vio la cara de sorpresa.
              </p>

              <div className="flex justify-center my-6" data-id="element-129">
                <div className="bg-green-50 px-8 py-4 rounded-full border border-green-200" data-id="element-130">
                  <span className="text-4xl font-bold text-green-700 font-serif" data-id="element-131">
                    128/82
                  </span>
                </div>
              </div>

              <p data-id="element-132">De 165/105 a 128/82 en un mes.</p>
              <p data-id="element-133">
                La Dra. Ramírez revisó el historial dos veces. "Gloria, ¿qué
                estás haciendo diferente?"
              </p>
              <p data-id="element-134">
                Cuando le contó sobre las gomitas, la doctora asintió: "Los
                estudios sobre nitratos de betabel son muy sólidos. Y si lo
                combinas con semilla de uva para la función endotelial... sigue
                con eso. Está funcionando."
              </p>
            </div>
          </div>

          <SectionHeading data-id="element-135">No Es Solo Gloria</SectionHeading>

          <p data-id="element-136">Busqué más testimonios. El patrón se repite:</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-8 not-prose" data-id="element-137">
            <TestimonialCard name="Alejandra" location="48, CDMX" quote="Ya no podía con mis piernas. Ya no las siento tan pesadas como antes." avatarUrl="https://cdn.magicpatterns.com/uploads/k59FXJw2nEpP6yJR1GPte2/Captura_de_pantalla_2026-01-23_a_las_23.01.38.png" data-id="element-138" />
            <TestimonialCard name="Teresa" location="56, Morelia" quote="Dormía mal y amanecía más cansada. Ahora despierto con energía." avatarUrl="https://cdn.magicpatterns.com/uploads/fQt1hTn8mz8AVnZSiAtPge/Captura_de_pantalla_2026-01-23_a_las_23.01.57.png" data-id="element-139" />
            <TestimonialCard name="Laura" location="53, Monterrey" quote="Mis hijas decían que ya no salía ni al mandado. Ahora las acompaño al tianguis." avatarUrl="https://cdn.magicpatterns.com/uploads/tGCkfbg3toW9Et4ySnLo19/Captura_de_pantalla_2026-01-23_a_las_23.02.13.png" data-id="element-140" />
            <TestimonialCard name="Margarita" location="60, Hermosillo" quote="Mi hija me las regaló y dudé. Ahora yo se las recomiendo a todas mis amigas." avatarUrl="https://cdn.magicpatterns.com/uploads/khjRK7pqfcwpTBvJPe2g4X/Captura_de_pantalla_2026-01-23_a_las_23.04.26.png" data-id="element-141" />
          </div>

          <div className="flex items-center justify-center gap-2 mb-8 font-bold text-slate-800" data-id="element-142">
            <div className="flex text-[#be6e8a]" data-id="element-143">
              <Star className="w-5 h-5 fill-current" data-id="element-144" />
              <Star className="w-5 h-5 fill-current" data-id="element-145" />
              <Star className="w-5 h-5 fill-current" data-id="element-146" />
              <Star className="w-5 h-5 fill-current" data-id="element-147" />
              <Star className="w-5 h-5 fill-current" data-id="element-148" />
            </div>
            <span data-id="element-149">4,718 reseñas. 4.9 estrellas.</span>
          </div>

          <figure className="my-8" data-id="element-150">
            <img src="https://cdn.magicpatterns.com/uploads/opKtaVR4ZNoVYUnA2KuQri/Warm_and_Refined_Angelitas_Logo_Design_(8).png" alt="Cómo usar Angelitas - Demostración del producto" className="w-full max-w-2xl mx-auto rounded-sm shadow-lg" data-id="element-151" />
          </figure>

          <p className="text-center italic text-slate-600 mb-12" data-id="element-152">
            94% reportan piernas más ligeras después de 2 semanas.
          </p>

          <SectionHeading data-id="element-153">La Advertencia De Gloria</SectionHeading>

          <p data-id="element-154">Antes de despedirnos, Gloria se puso seria:</p>

          <p data-id="element-155">"María Fernanda, quiero que pongas esto. Es importante."</p>

          <p data-id="element-156">
            "Si hubiera esperado un mes más... si ese martes hubiera sido un
            poquito peor... tal vez no estaría aquí. Tal vez mis hijas estarían
            organizando un funeral en lugar de preguntarme qué me tomé para
            verme más alegre."
          </p>

          <p data-id="element-157">Hizo una pausa.</p>

          <p data-id="element-158">
            "No quiero asustar a nadie. Pero tampoco quiero que otras mujeres
            ignoren las señales porque creen que es 'normal' o 'de la edad'. No
            es normal. No tiene que ser así. Y sí hay algo que puedes hacer."
          </p>

          <div className="bg-stone-900 text-stone-50 p-8 md:p-12 rounded-sm my-12 shadow-xl" data-id="element-159">
            <h2 className="text-2xl md:text-3xl font-serif font-bold mb-6 text-center text-white" data-id="element-160">
              Es Tu Decisión
            </h2>

            <div className="space-y-4 mb-8 text-lg" data-id="element-161">
              <p data-id="element-162">El cansancio que sientes no es normal.</p>
              <p data-id="element-163">Las piernas de plomo no son "de la edad".</p>
              <p data-id="element-164">La niebla mental no es algo con lo que tengas que vivir.</p>
              <p data-id="element-165">Y la presión alta no avisa hasta que es demasiado tarde.</p>
              <p className="font-bold text-[#be6e8a]" data-id="element-166">
                Gloria casi aprendió esto de la peor manera posible.
              </p>
              <p data-id="element-167">No tienes que esperar a tu propio susto para hacer algo.</p>
            </div>

            <p className="mb-8" data-id="element-168">
              Si quieres saber más sobre Angelitas — ver los ingredientes, leer
              más testimonios, verificar disponibilidad — puedes hacerlo ahora.
            </p>

            <p className="font-bold text-center mb-8 text-xl" data-id="element-169">
              Miles de mujeres mexicanas ya lo están usando.
              <br data-id="element-170" />
              <span className="text-[#be6e8a]" data-id="element-171">
                ¿Serás tú la próxima en recuperar su energía?
              </span>
            </p>

            <CTAButton fullWidth className="text-lg py-5 shadow-lg shadow-[#be6e8a]/20" data-id="element-172">
              HAZ CLIC AQUÍ PARA CONOCER ANGELITAS Y VER DISPONIBILIDAD
            </CTAButton>

            <p className="text-xs text-stone-400 mt-6 text-center leading-relaxed" data-id="element-173">
              P.D. — Angelitas tiene garantía de 30 días. Si no ves mejoras,
              pides tu dinero de vuelta. Sin preguntas. Gloria dijo: "Si hubiera
              esperado un mes más, quizás no estaría aquí." No dejes que el
              cansancio "normal" te siga robando días de vida.
            </p>
          </div>
        </article>

        {/* Footer */}
        <footer className="border-t border-stone-200 mt-16 pt-8 text-center text-xs text-stone-400" data-id="element-174">
          <p className="mb-4" data-id="element-175">
            Este sitio no es parte del sitio web de Facebook o Facebook Inc.
            Además, este sitio no está respaldado por Facebook de ninguna
            manera. FACEBOOK es una marca registrada de FACEBOOK, Inc.
          </p>
          <p data-id="element-176">
            &copy; {new Date().getFullYear()} Salud y Bienestar. Todos los
            derechos reservados.
          </p>
        </footer>
      </main>
    </div>;
}